/**
 * Composant pour l'invite d'installation PWA
 */
import React from 'react';
import { Download, X, Smartphone } from 'lucide-react';
import { usePWA } from '../hooks/usePWA';
import { logger } from '../utils/logger';
import { useTranslation } from 'react-i18next';

interface PWAInstallPromptProps {
  onClose?: () => void;
}

export const PWAInstallPrompt: React.FC<PWAInstallPromptProps> = ({ onClose }) => {
  const { t } = useTranslation('common');
  const { isInstallable, installApp, isOnline } = usePWA();
  const [isVisible, setIsVisible] = React.useState(false);
  const [isDismissed, setIsDismissed] = React.useState(false);

  React.useEffect(() => {
    // Afficher l'invite après 30 secondes si installable
    const timer = setTimeout(() => {
      if (isInstallable && !isDismissed) {
        setIsVisible(true);
      }
    }, 30000);

    return () => clearTimeout(timer);
  }, [isInstallable, isDismissed]);

  const handleInstall = async () => {
    try {
      await installApp();
      setIsVisible(false);
      onClose?.();
    } catch (error) {
      logger.error('Installation failed:', error);
    }
  };

  const handleDismiss = () => {
    setIsVisible(false);
    setIsDismissed(true);
    onClose?.();
    
    // Ne plus afficher pendant 7 jours
    localStorage.setItem('pwa-install-dismissed', Date.now().toString());
  };

  // Vérifier si l'invite a été récemment fermée
  React.useEffect(() => {
    const dismissedTime = localStorage.getItem('pwa-install-dismissed');
    if (dismissedTime) {
      const sevenDaysAgo = Date.now() - (7 * 24 * 60 * 60 * 1000);
      if (parseInt(dismissedTime) > sevenDaysAgo) {
        setIsDismissed(true);
      }
    }
  }, []);

  if (!isVisible || !isInstallable || isDismissed) {
    return null;
  }

  return (
    <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 z-50">
      <div className="bg-white rounded-lg shadow-xl border border-slate-200 p-4">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center">
            <div className="bg-blue-100 rounded-full p-2 mr-3">
              <Smartphone className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h3 className="font-bold text-slate-900">
                {t('pwa.installTitle')}
              </h3>
              <p className="text-sm text-slate-600">
                {t('pwa.installSubtitle')}
              </p>
            </div>
          </div>
          <button
            onClick={handleDismiss}
            className="text-slate-400 hover:text-slate-600 transition-colors"
            aria-label={t('pwa.closeInstall')}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="mb-4">
          <ul className="text-sm text-slate-600 space-y-1">
            <li>• {t('pwa.features.offline')}</li>
            <li>• {t('pwa.features.notifications')}</li>
            <li>• {t('pwa.features.mobile')}</li>
            <li>• {t('pwa.features.speed')}</li>
          </ul>
        </div>

        <div className="flex space-x-3">
          <button
            onClick={handleInstall}
            disabled={!isOnline}
            className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
          >
            <Download className="w-4 h-4 mr-2" />
            {t('pwa.installBtn')}
          </button>
          <button
            onClick={handleDismiss}
            className="px-4 py-2 text-slate-600 hover:text-slate-800 transition-colors"
          >
            {t('pwa.laterBtn')}
          </button>
        </div>

        {!isOnline && (
          <p className="text-xs text-amber-600 mt-2 text-center">
            {t('pwa.connectionRequired')}
          </p>
        )}
      </div>
    </div>
  );
};

// Composant pour l'indicateur de mise à jour
export const PWAUpdatePrompt: React.FC = () => {
  const { t } = useTranslation('common');
  const { updateAvailable, updateApp } = usePWA();
  const [isVisible, setIsVisible] = React.useState(false);

  React.useEffect(() => {
    if (updateAvailable) {
      setIsVisible(true);
    }
  }, [updateAvailable]);

  const handleUpdate = () => {
    updateApp();
    setIsVisible(false);
  };

  const handleDismiss = () => {
    setIsVisible(false);
  };

  if (!isVisible || !updateAvailable) {
    return null;
  }

  return (
    <div className="fixed top-4 left-4 right-4 md:left-auto md:right-4 md:w-96 z-50">
      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
        <div className="flex items-start justify-between mb-3">
          <div>
            <h3 className="font-bold text-green-900">
              {t('pwa.updateTitle')}
            </h3>
            <p className="text-sm text-green-700">
              {t('pwa.updateSubtitle')}
            </p>
          </div>
          <button
            onClick={handleDismiss}
            className="text-green-400 hover:text-green-600 transition-colors"
            aria-label={t('pwa.closeUpdate')}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex space-x-3">
          <button
            onClick={handleUpdate}
            className="flex-1 bg-green-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-green-700 transition-colors"
          >
            {t('pwa.updateBtn')}
          </button>
          <button
            onClick={handleDismiss}
            className="px-4 py-2 text-green-600 hover:text-green-800 transition-colors"
          >
            {t('pwa.ignoreBtn')}
          </button>
        </div>
      </div>
    </div>
  );
};

// Composant pour l'indicateur hors ligne
export const OfflineIndicator: React.FC = () => {
  const { t } = useTranslation('common');
  const { isOnline } = usePWA();
  const [showOffline, setShowOffline] = React.useState(false);

  React.useEffect(() => {
    if (!isOnline) {
      setShowOffline(true);
    } else {
      // Masquer après 3 secondes quand on revient en ligne
      const timer = setTimeout(() => setShowOffline(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [isOnline]);

  if (!showOffline) {
    return null;
  }

  return (
    <div className="fixed top-0 left-0 right-0 z-50">
      <div className={`px-4 py-2 text-center text-sm font-medium transition-colors ${
        isOnline 
          ? 'bg-green-600 text-white' 
          : 'bg-amber-600 text-white'
      }`}>
        {isOnline ? (
          `✅ ${t('offlineNotification.connectedTitle')}`
        ) : (
          t('pwa.offlineLimited')
        )}
      </div>
    </div>
  );
};
